import React, { useState, useEffect } from 'react';
import { pushEvent } from '../../utils/analytics';
import { trackUtmParameters } from '../../utils/utmTracker';
import {
  SmsConsentCheckbox,
  buildSmsConsentPayload,
  shouldShowSmsConsent,
} from '../../components/SmsConsent';
import '../EnterprisePage.css';
import './InvestorPage.css';

const CALENDLY_URL = 'https://calendly.com/lumi-rob/60-minute-meeting';
const API_URL = process.env.REACT_APP_API_URL;
const CONTACT_API = `${API_URL}/users/contact`;
const EMAIL_DESTINATION = 'rob@botspot.trade';

export default function InvestorPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    note: '',
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [smsConsentAccepted, setSmsConsentAccepted] = useState(false);
  const [smsConsentError, setSmsConsentError] = useState('');
  const [submitted, setSubmitted] = useState(false);

  // Load Calendly widget script
  useEffect(() => {
    const head = document.querySelector('head');
    const script = document.createElement('script');
    script.setAttribute('src', 'https://assets.calendly.com/assets/external/widget.js');
    script.setAttribute('type', 'text/javascript');
    script.async = true;
    head.appendChild(script);
    return () => {
      if (head && script) {
        head.removeChild(script);
      }
    };
  }, []);

  // Re-initialize Calendly widget when success state renders
  useEffect(() => {
    if (submitted && window.Calendly) {
      window.Calendly.initInlineWidget({
        url: `${CALENDLY_URL}?background_color=1a1a1a&text_color=ffffff`,
        parentElement: document.querySelector('.enterprise-page-calendly .calendly-inline-widget'),
      });
    }
  }, [submitted]);

  useEffect(() => {
    pushEvent({ event: 'investor_page_view', source: 'investor_page' });
    trackUtmParameters();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (name === 'phone' && smsConsentAccepted) {
      setSmsConsentAccepted(false);
    }
    if (name === 'phone' && smsConsentError) {
      setSmsConsentError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!formData.name.trim() || !formData.email.trim()) {
      setError('Please fill in all required fields');
      setLoading(false);
      return;
    }

    try {
      const utmSource = localStorage.getItem('utm_source') || '';
      const utmMedium = localStorage.getItem('utm_medium') || '';
      const utmCampaign = localStorage.getItem('utm_campaign') || '';
      const utmContent = localStorage.getItem('utm_content') || '';

      const response = await fetch(CONTACT_API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: formData.email,
          firstName: formData.name,
          phone: formData.phone,
          ...(shouldShowSmsConsent(formData.phone) ? {
            smsConsent: buildSmsConsentPayload({
              source: 'investor_page_contact_form',
              phoneNumber: formData.phone,
              accepted: smsConsentAccepted,
            }),
          } : {}),
          message: formData.note,
          isInvestor: true,
          utmSource: utmSource || 'investor_page',
          utmMedium,
          utmCampaign: utmCampaign || 'investor',
          utmContent,
        }),
      });

      if (!response.ok) {
        throw new Error('Submission failed');
      }

      pushEvent({
        event: 'InvestorFormSubmit',
        source: 'investor_page',
      });

      setSubmitted(true);
    } catch (err) {
      setError('Something went wrong. Please try again or email us directly.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="enterprise-page">
        <section className="enterprise-page-final-cta">
          <div className="enterprise-page-form-success" data-testid="investor-form-success">
            <h3>Thanks! We'll be in touch soon.</h3>
            <p>Want to book a time right now? Pick a slot below.</p>
            <div className="enterprise-page-calendly">
              <div
                className="calendly-inline-widget"
                data-url={`${CALENDLY_URL}?background_color=1a1a1a&text_color=ffffff`}
                style={{ minWidth: '320px', height: '700px' }}
              />
            </div>
          </div>
          <div className="enterprise-page-contact-alt">
            <p>
              Prefer email? Reach us at{' '}
              <a href={`mailto:${EMAIL_DESTINATION}`}>{EMAIL_DESTINATION}</a>
            </p>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="enterprise-page">
      {/* HERO + FORM (mirrors Enterprise Section 8: Final CTA) */}
      <section className="enterprise-page-final-cta" id="investor-contact">
        <h2>Are you interested in investing in BotSpot?</h2>
        <p>
          Fill out the form below and book a call with Robert to learn more.
        </p>

        {error && <p className="enterprise-page-form-error">{error}</p>}

        <form
          className="enterprise-page-form"
          onSubmit={handleSubmit}
          data-testid="investor-form"
        >
          <div className="enterprise-page-form-group">
            <label className="enterprise-page-form-label" htmlFor="inv-name">
              Your Name *
            </label>
            <input
              id="inv-name"
              className="enterprise-page-form-input"
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="Jane Smith"
              required
            />
          </div>

          <div className="enterprise-page-form-group">
            <label className="enterprise-page-form-label" htmlFor="inv-email">
              Email Address *
            </label>
            <input
              id="inv-email"
              className="enterprise-page-form-input"
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="jane@example.com"
              required
            />
          </div>

          <div className="enterprise-page-form-group">
            <label className="enterprise-page-form-label" htmlFor="inv-phone">
              Phone Number
            </label>
            <input
              id="inv-phone"
              className="enterprise-page-form-input"
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              placeholder="(555) 123-4567"
            />
          </div>

          {shouldShowSmsConsent(formData.phone) ? (
            <SmsConsentCheckbox
              id="inv-sms-consent"
              checked={smsConsentAccepted}
              onChange={(checked) => {
                setSmsConsentAccepted(checked);
                if (checked) setSmsConsentError('');
              }}
              disabled={loading}
              error={smsConsentError}
            />
          ) : null}

          <div className="enterprise-page-form-group">
            <label className="enterprise-page-form-label" htmlFor="inv-note">
              Any questions or concerns about investing in BotSpot?{' '}
              <span style={{ fontWeight: 400, color: 'rgba(255,255,255,0.4)' }}>
                (optional)
              </span>
            </label>
            <textarea
              id="inv-note"
              className="enterprise-page-form-textarea"
              name="note"
              value={formData.note}
              onChange={handleInputChange}
              placeholder="e.g. I'm interested in early-stage fintech companies..."
            />
          </div>

          <button
            type="submit"
            className="enterprise-page-form-submit"
            disabled={loading}
          >
            {loading ? 'Submitting...' : 'Book a Call'}
          </button>
        </form>

        <div className="enterprise-page-contact-alt">
          <p>
            Prefer email? Reach us at{' '}
            <a href={`mailto:${EMAIL_DESTINATION}`}>{EMAIL_DESTINATION}</a>
          </p>
        </div>
      </section>

      <div className="enterprise-page-divider" />

      {/* VALUE PROPOSITION (uses enterprise card layout) 
      <section className="enterprise-page-section">
        <div className="enterprise-page-section-header">
          <h2>Why Invest in BotSpot</h2>
        </div>
        <div className="enterprise-page-problems">
          <div className="enterprise-page-problem-card">
            <i className="fas fa-robot enterprise-page-problem-card-icon" />
            <h3>What is BotSpot?</h3>
            <p>
              An AI-powered platform that enables users to design, deploy, and
              run automated trading strategies using natural language.
            </p>
          </div>
          <div className="enterprise-page-problem-card">
            <i className="fas fa-chart-line enterprise-page-problem-card-icon" />
            <h3>Why Investors Are Interested</h3>
            <ul className="investor-page-value-list">
              <li>Large and rapidly growing retail trading automation market</li>
              <li>AI-native workflow for strategy creation and deployment</li>
              <li>Infrastructure layer for next-generation algorithmic trading</li>
            </ul>
          </div>
          <div className="enterprise-page-problem-card">
            <i className="fas fa-seedling enterprise-page-problem-card-icon" />
            <h3>Stage</h3>
            <p>
              Early-stage company actively building product and onboarding
              early users.
            </p>
          </div>
        </div>
      </section> */}
    </div>
  );
}
